// customer-website/src/pages/SubscriptionErrorPage.tsx
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';

export default function SubscriptionErrorPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [canClose, setCanClose] = useState(true);
  const errorMessage = searchParams.get('message') || 'An unexpected error occurred during payment processing';

  useEffect(() => {
    console.log('[SubscriptionError] Page loaded with error:', errorMessage);

    // ✅ MOBILE BROWSER: Check if returning from mobile redirect (same-page)
    const checkoutPending = sessionStorage.getItem('checkout_pending');
    const returnUrl = sessionStorage.getItem('checkout_return_url');

    if (checkoutPending === 'true' && returnUrl) {
      console.log('[SubscriptionError] Mobile browser redirect detected - returning to:', returnUrl);

      // Clear checkout state
      sessionStorage.removeItem('checkout_pending');
      sessionStorage.removeItem('checkout_return_url');

      // Store error flag for useMobileCheckoutReturn hook
      sessionStorage.setItem('checkout_completed', 'error');
      sessionStorage.setItem('checkout_error_message', errorMessage);

      // Redirect back to original page
      window.location.href = returnUrl;
      return;
    }

    // ✅ WEB: Check if opened from web app (has window.opener)
    if (window.opener && !window.opener.closed) {
      try {
        console.log('[SubscriptionError] Sending error message to parent window');

        // Send message to parent - same domain (www.wlkthru.co -> www.wlkthru.co)
        window.opener.postMessage(
          { type: 'STRIPE_CHECKOUT_ERROR', error: errorMessage },
          window.location.origin
        );

        // Try to close this tab after brief delay (let user see error)
        setTimeout(() => {
          console.log('[SubscriptionError] Attempting to close tab...');

          // Try multiple times to close
          window.close();
          setTimeout(() => window.close(), 100);
          setTimeout(() => window.close(), 300);

          // If we're still here after 1 second, window.close() failed
          setTimeout(() => {
            if (!window.closed) {
              setCanClose(false);
              console.log('[SubscriptionError] Auto-close failed, showing manual close button');
            }
          }, 1000);
        }, 1500); // 1.5s delay so user can see the error
      } catch (error) {
        console.error('[SubscriptionError] Failed to send message to parent:', error);
        setCanClose(false);
      }
    }
    // ✅ MOBILE: Check if running inside React Native WebView
    else if (typeof window !== 'undefined' && (window as any).ReactNativeWebView) {
      try {
        console.log('[SubscriptionError] Sending message to React Native WebView');
        (window as any).ReactNativeWebView.postMessage(
          JSON.stringify({
            type: 'payment_error',
            message: errorMessage
          })
        );

        // Mobile app will handle navigation
        setTimeout(() => {
          window.location.href = 'wlkthrumobile://subscription-error';
        }, 500);
      } catch (error) {
        console.log('[SubscriptionError] Not running in React Native WebView:', error);
      }
    }
    // ✅ FALLBACK: Direct navigation (no parent window)
    else {
      console.log('[SubscriptionError] No parent window - user navigated directly');
      setCanClose(false);
    }
  }, [errorMessage]);

  const handleRetry = () => {
    navigate('/profile?tab=billing');
  };

  // Minimal UI
  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-white flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center space-y-6 animate-scale-in">
        {/* Error Icon */}
        <div className="flex justify-center">
          <div className="relative">
            <div className="w-24 h-24 bg-gradient-to-br from-red-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
              {canClose ? (
                <Loader2 className="w-14 h-14 text-white animate-spin" />
              ) : (
                <AlertCircle className="w-14 h-14 text-white" strokeWidth={2.5} />
              )}
            </div>
          </div>
        </div>

        {/* Title */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {canClose ? 'Processing...' : 'Payment Failed'}
          </h1>
          <p className="text-gray-600">
            {canClose
              ? 'Completing checkout...'
              : 'We couldn\'t process your payment'
            }
          </p>
        </div>

        {/* Error message */}
        {!canClose && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
            <p className="text-sm text-red-800">{errorMessage}</p>
          </div>
        )}

        {/* Actions */}
        {!canClose && (
          <div className="space-y-3">
            {window.opener ? (
              // If has parent window, show close button
              <>
                <p className="text-sm text-gray-500 mb-2">
                  You can close this tab and try again in the main window
                </p>
                <Button
                  onClick={() => window.close()}
                  variant="outline"
                  className="w-full"
                >
                  Close This Tab
                </Button>
              </>
            ) : (
              // If no parent window (direct navigation), show retry button
              <Button
                onClick={handleRetry}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
